import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Trash2, Plus } from 'lucide-react';
import AlertModal from '../AlertModal';
import useLocalization from '../../../hooks/useLocalization';

const defaultquestions = {
  title: '',
  description: '',
  groups: [],
  footer: ''
};

export default function QuestionBuilder({ data = defaultquestions, onSubmit, disabled }) {
  const [questions, setQuestions] = useState(data);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const { translate, isRTL } = useLocalization();

  const addGroup = () => {
    const newGroup = {
      name: '',
      questions: []
    };
    setQuestions(prev => ({
      ...prev,
      groups: [...prev.groups, newGroup]
    }));
  };

  const updateGroup = (groupIndex, name) => {
    setQuestions(prev => ({
      ...prev,
      groups: prev.groups.map((group, index) => (index === groupIndex ? { ...group, name } : group))
    }));
  };

  const confirmDeleteGroup = groupIndex => {
    setGroupToDelete(groupIndex);
    setShowDeleteModal(true);
  };

  const handleDeleteGroup = () => {
    if (groupToDelete !== null) {
      setQuestions(prev => ({
        ...prev,
        groups: prev.groups.filter((group, index) => index !== groupToDelete)
      }));
      setShowDeleteModal(false);
      setGroupToDelete(null);
    }
  };

  const cancelDeleteGroup = () => {
    setShowDeleteModal(false);
    setGroupToDelete(null);
  };

  const addQuestion = groupIndex => {
    const newQuestion = {
      type: 'short',
      question: ''
    };
    setQuestions(prev => ({
      ...prev,
      groups: prev.groups.map((group, index) =>
        index === groupIndex ? { ...group, questions: [...group.questions, newQuestion] } : group
      )
    }));
  };

  const updateQuestion = (groupIndex, questionIndex, updates) => {
    setQuestions(prev => ({
      ...prev,
      groups: prev.groups.map((group, gIndex) =>
        gIndex === groupIndex
          ? {
              ...group,
              questions: group.questions.map((question, qIndex) =>
                qIndex === questionIndex ? { ...question, ...updates } : question
              )
            }
          : group
      )
    }));
  };

  const handleSubmit = () => {
    onSubmit(questions);
  };

  const deleteQuestion = (groupIndex, questionIndex) => {
    setQuestions(prev => ({
      ...prev,
      groups: prev.groups.map((group, gIndex) =>
        gIndex === groupIndex
          ? { ...group, questions: group.questions.filter((q, qIndex) => qIndex !== questionIndex) }
          : group
      )
    }));
  };

  return (
    <div className="p-2">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <Card>
          <CardContent className="space-y-4">
            <div className="w-full flex flex-col gap-2">
              <label className="font-medium flex">{translate('title') || 'Title'}</label>
              <Input
                placeholder={translate('enter_questions_title')}
                value={questions.title}
                onChange={e => setQuestions(prev => ({ ...prev, title: e.target.value }))}
                className="border border-bl"
              />
            </div>
            <div className="w-full flex flex-col gap-2">
              <label className="font-medium flex">{translate('description')}</label>
              <Textarea
                placeholder={translate('enter_questions_description')}
                value={questions.description}
                onChange={e => setQuestions(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                className="border border-bl"
              />
            </div>
          </CardContent>
        </Card>

        {/* Question Groups Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{translate('question_groups')}</CardTitle>
            <Button onClick={addGroup} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              {translate('add_group')}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse no-sticky">
                <thead>
                  <tr className="border-b bg-gray-200 text-start">
                    <th className={`${isRTL ? 'text-end' : 'text-start'} p-3 font-medium w-64`}>
                      {translate('group_name')}
                    </th>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} p-3 font-medium`}>{translate('questions')}</th>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} p-3 font-medium w-20`}>{translate('type')}</th>
                    <th className="text-center p-3 font-medium w-24">{translate('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.groups.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center p-8 text-muted-foreground">
                        {translate('no_groups_created_yet')}
                      </td>
                    </tr>
                  ) : (
                    questions.groups.map((group, groupIndex) => (
                      <>
                        {/* Group Header Row */}
                        <tr key={groupIndex} className="border-b bg-muted/50">
                          <td className="p-3">
                            <Input
                              placeholder={translate('enter_group_name')}
                              value={group.name}
                              onChange={e => updateGroup(groupIndex, e.target.value)}
                              className="font-medium border border-bl"
                            />
                          </td>
                          <td className="p-3 flex">
                            <Button onClick={() => addQuestion(groupIndex)} size="sm" variant="outline">
                              <Plus className="w-4 h-4 mr-2" />
                              {translate('add_question')}
                            </Button>
                          </td>
                          <td className="p-3"></td>
                          <td className="p-3 text-center">
                            <Button onClick={() => confirmDeleteGroup(groupIndex)} size="sm" variant="destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>

                        {/* Questions Rows */}
                        {group.questions.map((question, questionIndex) => (
                          <tr
                            key={`${groupIndex}-${questionIndex}`}
                            className="border-b border-gray-300 hover:bg-muted/30"
                          >
                            <td className="p-3 text-center">
                              <span className="text-sm font-medium">Q{questionIndex + 1}</span>
                            </td>
                            <td className="p-3">
                              <Input
                                placeholder={translate('enter_question_text')}
                                value={question.question}
                                onChange={e => updateQuestion(groupIndex, questionIndex, { question: e.target.value })}
                                className="border border-bl"
                              />
                            </td>
                            <td className="p-3">
                              <Select
                                value={question.type}
                                onValueChange={value => updateQuestion(groupIndex, questionIndex, { type: value })}
                              >
                                <SelectTrigger className="border border-bl">
                                  <SelectValue placeholder={translate('select_one')} />
                                </SelectTrigger>
                                <SelectContent className="z-30 bg-white">
                                  <SelectItem value="short">{translate('short')}</SelectItem>
                                  <SelectItem value="long">{translate('long')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 text-center">
                              <Button
                                onClick={() => deleteQuestion(groupIndex, questionIndex)}
                                size="sm"
                                variant="ghost"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <Card className="gap-2">
          <CardHeader>
            <CardTitle className={`${isRTL ? 'text-end' : 'text-start'}`}>{translate('footer')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder={translate('enter_footer_text')}
              value={questions.footer}
              onChange={e => setQuestions(prev => ({ ...prev, footer: e.target.value }))}
              rows={4}
              className="border border-bl"
            />
          </CardContent>
        </Card>

        {/* Save Actions */}
        <div className="flex gap-4 justify-end mt-4">
          <Button onClick={handleSubmit} disabled={disabled}>
            {translate('save')}
          </Button>
        </div>
      </div>

      <AlertModal
        show={showDeleteModal}
        onHide={cancelDeleteGroup}
        onContinueClick={handleDeleteGroup}
        alertText={translate('are_you_sure_you_want_to_delete_this_group')}
        continueButtonText={translate('delete')}
        continueButtonVariant="success text-white"
      />
    </div>
  );
}
